import clsx from 'clsx';
import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { aggregate, suffixId, useComposedRefs, useControlledValue, useElementSize, useResponsiveProp, } from '../../utils';
import { Tag } from '../Tag/Tag';
import { TextField } from '../TextField';
import './TagField.css';
export const TagField = forwardRef((props, ref) => {
    const { id: userId, name, value, defaultValue = [], inputValue, onChange, delimiter = ',', disabled, inputRef: userInputRef, autoFocus, onKeyDown, onDismiss, noWrap = { extraSmall: true }, UNSAFE_inputClassName, UNSAFE_textFieldClassName, UNSAFE_textFieldRef, ...rest } = props;
    const uniqueId = useId();
    const id = userId !== null && userId !== void 0 ? userId : uniqueId;
    const inputRef = useRef(null);
    const elementRef = useRef(null);
    const [isKeyReleased, setIsKeyReleased] = useState(false);
    const [input, setInput] = useState('');
    const responsiveNoWrap = useResponsiveProp(noWrap);
    const [tags, setTags] = useControlledValue({
        value,
        onChange,
        defaultValue,
    });
    const isValidKey = (key) => {
        if (key === 'Enter') {
            return true;
        }
        if (typeof delimiter === 'string') {
            return key === delimiter;
        }
        return delimiter.test(key);
    };
    useElementSize(elementRef, (_size, element) => {
        if (inputRef.current) {
            element.scrollLeft = inputRef.current.offsetLeft;
        }
    });
    useEffect(() => {
        if (elementRef.current && inputRef.current) {
            elementRef.current.scrollLeft = inputRef.current.offsetLeft;
        }
    }, [tags]);
    return (React.createElement(React.Fragment, null,
        name && React.createElement("input", { type: "hidden", name: name, value: tags.join(',') }),
        React.createElement(TextField, { ...rest, id: id, disabled: disabled, value: inputValue !== null && inputValue !== void 0 ? inputValue : input, size: input.length || 1, onChange: (e) => {
                setInput(e.target.value);
            }, onKeyDown: onKeyDown
                ? onKeyDown
                : (e) => {
                    var _a;
                    const { key } = e;
                    const trimmedInput = input.trim();
                    if (isValidKey(key) && trimmedInput.length) {
                        e.preventDefault();
                        setInput('');
                        if (!tags.includes(trimmedInput)) {
                            setTags((prevTags) => [...prevTags, trimmedInput]);
                        }
                    }
                    if (key === 'Backspace' &&
                        !input.length &&
                        tags.length &&
                        isKeyReleased) {
                        e.preventDefault();
                        const tagsCopy = [...tags];
                        const poppedTag = (_a = tagsCopy.pop()) !== null && _a !== void 0 ? _a : '';
                        setTags(tagsCopy);
                        setInput(poppedTag);
                    }
                    setIsKeyReleased(false);
                }, onKeyUp: onKeyDown
                ? undefined
                : (e) => {
                    var _a;
                    setIsKeyReleased(true);
                    // @ts-expect-error onKeyUp not in own props type
                    (_a = rest.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(rest, e);
                }, onBlur: (e) => {
                var _a;
                const trimmedInput = input.trim();
                setInput('');
                if (trimmedInput.length && !tags.includes(trimmedInput)) {
                    setTags((prevTags) => [...prevTags, trimmedInput]);
                }
                // @ts-expect-error onBlur not in own props type
                (_a = rest.onBlur) === null || _a === void 0 ? void 0 : _a.call(rest, e);
            }, inputRef: useComposedRefs(inputRef, userInputRef), autoFocus: autoFocus, UNSAFE_textFieldClassName: clsx('mollie-ui-tag-field', {
                'mollie-ui-tag-field--disabled': !!disabled,
                'mollie-ui-tag-field--nowrap': !!responsiveNoWrap,
            }, UNSAFE_textFieldClassName), UNSAFE_textFieldRef: useComposedRefs(elementRef, UNSAFE_textFieldRef), UNSAFE_inputClassName: clsx('mollie-ui-tag-field-input', UNSAFE_inputClassName), UNSAFE_beforeInput: React.createElement(React.Fragment, null, tags.map((value) => (React.createElement(Tag, { key: value, disabled: disabled, dismissable: true, onDismiss: () => {
                    var _a;
                    setTags((prevTags) => prevTags.filter((tag) => tag !== value));
                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss(value);
                }, className: "mollie-ui-tag-field-tag", id: suffixId(id, encodeURI(value)) }, value)))), UNSAFE_textFieldOnClick: (e) => {
                var _a, _b;
                if (inputRef.current &&
                    !inputRef.current.contains(e.target)) {
                    e.preventDefault();
                    e.stopPropagation();
                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.dispatchEvent(new Event('click', e));
                }
            }, UNSAFE_textFieldOnMouseDown: (e) => {
                if (inputRef.current &&
                    !inputRef.current.contains(e.target)) {
                    e.preventDefault();
                    e.stopPropagation();
                    inputRef.current.dispatchEvent(new Event('mousedown', e));
                }
            }, ref: ref, "aria-labelledby": aggregate(...tags.map((value) => suffixId(id, encodeURI(value)))) })));
});
//# sourceMappingURL=TagField.js.map